import { useMemo } from "react";

import { CURRENCY_CODE, DEFAULT_SCENARIO_SETTINGS } from "consts";

import { useMainCropLabelLookup } from "pages/app/scenario-analysis/hooks/useMainCropOptions";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { buildForecastDataFromSoilSight } from "pages/app/scenario-analysis/utils/forecast";
import { ScenarioPrintState } from "pages/app/scenario-analysis/utils/printState";
import { toTimelineOption } from "pages/app/scenario-analysis/utils/projectSettings";
import { buildScenarioComparison, canCompareWithBaseline } from "pages/app/scenario-analysis/utils/scenarioComparison";
import { getReferenceScenario } from "pages/app/scenario-analysis/utils/scenarioValidation";

/** Everything the PDF report shows, derived from the print state the outcomes page hands over. */
export const usePdfReportData = (printState: ScenarioPrintState) => {
  const locationScenarios = printState?.scenarios;
  const locationOutcomes = printState?.soilSightOutcomes;
  const locationResilienceScores = printState?.resilienceScores;

  const scenarios: ScenarioData[] = useMemo(() => locationScenarios ?? [], [locationScenarios]);
  const soilSightOutcomes = useMemo(() => locationOutcomes ?? null, [locationOutcomes]);
  const resilienceScores = useMemo(() => locationResilienceScores ?? {}, [locationResilienceScores]);
  const settings = printState?.settings ?? DEFAULT_SCENARIO_SETTINGS;
  const currency = printState?.currency ?? CURRENCY_CODE.EUR;

  const projectionYears = toTimelineOption(Number(settings.timeline));

  const { mainCropLabelLookup, isPracticeCropsFetched } = useMainCropLabelLookup();

  const forecastData = useMemo(
    () => buildForecastDataFromSoilSight(scenarios, projectionYears, soilSightOutcomes ?? {}),
    [scenarios, projectionYears, soilSightOutcomes]
  );

  const baseline = useMemo(() => getReferenceScenario(scenarios) ?? null, [scenarios]);

  // One key insights row per change, under the same rule the comparison box uses.
  const keyInsightRows = useMemo(() => {
    if (!baseline) return [];
    return scenarios
      .filter((scenario) => canCompareWithBaseline(baseline, scenario, soilSightOutcomes))
      .map((scenario) => ({
        scenario,
        comparison: buildScenarioComparison({
          outcomes: soilSightOutcomes,
          baselineScenarioId: baseline.id,
          comparisonScenarioId: scenario.id,
          projectionYears,
        }),
      }));
  }, [baseline, scenarios, soilSightOutcomes, projectionYears]);

  return {
    scenarios,
    soilSightOutcomes,
    resilienceScores,
    settings,
    currency,
    projectionYears,
    mainCropLabelLookup,
    isPracticeCropsFetched,
    forecastData,
    baseline,
    keyInsightRows,
  };
};
